<template>
    <div>
        <div class="grid calculator_earnings__form">
            <div class="col-12 md:col-6 xl:col-4">
                <div class="px-0 sm:px-3">
                    <span class="text-base text-color-secondary inline-block mb-3">{{ $t('lang.pages.referal.calculator_earnings.select_tariff') }}</span>
                    <div class="formgrid">
                        <template v-for="tariff in tariffTemplates">
                            <div
                                class="field-radiobutton"
                                :class="{'calculator_earnings__active_tariff': (model.tariff === tariff.id)}"
                            >
                                <RadioButton
                                    v-model="model.tariff"
                                    :inputId="getUID(tariff.id)"
                                    name="tariff"
                                    :value="tariff.id"
                                />
                                <label class="flex justify-content-between align-items-center flex-wrap w-full cursor-pointer gap-2" :for="getUID(tariff.id)">
                                    <span class="calculator_earnings__tariff_name font-bold text-lg">{{ tariff.label }}</span>
                                    <span class="text-color-secondary">{{ getPrice(tariff.price) }}</span>
                                </label>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
            <div class="col-12 md:col-6 xl:col-4">
                <div class="calculator_earnings__sliders flex flex-column h-full w-full xl:w-9 px-0 sm:px-3">
                    <div style="margin-bottom: 55px;">
                        <span class="text-base text-color-secondary block mb-3">{{ $t('lang.pages.referal.calculator_earnings.select_srok') }}</span>
                        <span class="text-lg block font-bold mb-3">{{ $t('lang.pages.referal.calculator_earnings.month_count', model.srok) }}</span>
                        <Slider v-model="model.srok" :min="1" :max="12" class="w-full mb-2" />
                        <div class="flex justify-content-between text-xs">
                            <span class="text-color-secondary">1</span>
                            <span class="text-color-secondary">12</span>
                        </div>
                    </div>
                    <div>
                        <span class="text-base text-color-secondary block mb-3">{{ $t('lang.pages.referal.calculator_earnings.select_users') }}</span>
                        <span class="text-lg block font-bold mb-3">{{ model.users }}</span>
                        <Slider v-model="model.users" :min="1" :max="100" class="w-full mb-2" />
                        <div class="flex justify-content-between text-xs">
                            <span class="text-color-secondary">1</span>
                            <span class="text-color-secondary">100</span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12 xl:col-4">
                <div class="flex h-full align-items-center justify-content-center">
                    <div class="calculator_earnings__earnings_content">
                        <span class="block mt-4 text-primary text-xl">{{ $t('lang.pages.referal.calculator_earnings.earnings') }}</span>
                        <span class="font-bold text-6xl">{{ getEarning }}</span>
                    </div>
                </div>
            </div>
        </div>
        <div class="calculator_earnings__notify">
            <p>{{ $t('lang.pages.referal.calculator_earnings.notify') }}</p>
        </div>
        <div class="grid">
            <div class="calculator_earnings__example col-12 md:col-6">
                <p class="text-2xl font-bold text-white">{{ $t('lang.pages.referal.calculator_earnings.example.title') }}</p>
                <span class="text-base block mb-2">{{ $t('lang.pages.referal.calculator_earnings.example.client') }}</span>
                <span class="text-lg block text-white">{{ $t('lang.pages.referal.calculator_earnings.example.tariff') }}</span>
                <div class="calculator_earnings__separator w-full md:w-9"></div>
                <span class="text-base block mb-2">{{ $t('lang.pages.referal.calculator_earnings.example.tariff_cost') }}<label class="text-lg font-bold text-white">{{ yearProfPrice }}</label></span>
                <span class="text-base block mb-2">{{ yearProfPrice }} * {{ $t('lang.pages.referal.calculator_earnings.example.two_years') }} = <label class="text-lg font-bold text-white">{{ twoYearProfPrice }}</label></span>
                <div class="calculator_earnings__separator w-full md:w-9"></div>
                <span class="text-base block mb-2">{{ $t('lang.pages.referal.calculator_earnings.example.earnings') }}</span>
                <p class="font-bold text-4xl text-white mt-0 pt-2">{{ twoYearProfEarnings }}</p>
                <Button
                    v-if="isUserAuth"
                    :label="$t('lang.pages.referal.calculator_earnings.example.partner_button')"
                    class="font-bold"
                    @click="onClickPartner"
                />
                <Button
                    v-else
                    :label="$t('lang.pages.referal.calculator_earnings.example.start_button')"
                    class="font-bold"
                    @click="onClickStartPartner"
                />
            </div>
            <div class="calculator_earnings__tops col-12 md:col-6">
                <p class="text-2xl font-bold text-white">{{ $t('lang.pages.referal.calculator_earnings.top_title') }}</p>
                <template v-for="item in topEarnings">
                    <div class="my-3 px-2 flex justify-content-between w-full md:w-9">
                        <span class="text-white">{{ item.email }}</span>
                        <span class="text-white font-bold">{{ getPrice(item.sm) }}</span>
                    </div>
                    <div class="calculator_earnings__separator w-full md:w-9"></div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import RadioButton from "primevue/radiobutton";
import uniqueId from "lodash/uniqueId";
import {FormatHelper} from "../../helpers/FormatHelper";
import Slider from 'primevue/slider';
import {ArrayHelper} from "../../helpers/ArrayHelper";
import Button from "primevue/button";

export default {
    name: "ReferalCalculatorEarnings",
    props: {
        tariffTemplates: {
            type: Object,
            required: true,
        },
        topEarnings: {
            type: Array,
            required: true,
        }
    },
    data() {
        return {
            model: {
                tariff: 5,
                srok: 12,
                users: 70,
            },
            earnings: 0,
            yearProfPrice: '',
            twoYearProfPrice: '',
            twoYearProfEarnings: '',
            uid: uniqueId('calculator-tariff'),
        }
    },
    mounted() {
        const tariff = ArrayHelper.getObjectByAttrValue(Object.values(this.tariffTemplates), 'name', 'professional');
        this.yearProfPrice = FormatHelper.number((tariff.price * 12), this.$t('lang.main.currency.sign'));
        this.twoYearProfPrice = FormatHelper.number((tariff.price * 24), this.$t('lang.main.currency.sign'));
        this.twoYearProfEarnings = FormatHelper.number((tariff.price * 24 * 0.2), this.$t('lang.main.currency.sign'));
        this.calcEarnings();
    },
    computed: {
        getEarning() {
            return FormatHelper.number(this.earnings, this.$t('lang.main.currency.sign'));
        },
        isUserAuth() {
            return this.$store.getters.IS_USER_AUTHENTICATED;
        },
    },
    methods: {
        getUID(slug) {
            return this.uid + '_' + slug;
        },
        getPrice(price) {
            return FormatHelper.number(price, this.$t('lang.main.currency.sign'));
        },
        calcEarnings() {
            const tariff = this.tariffTemplates[this.model.tariff];
            this.earnings = tariff.price * this.model.srok * this.model.users * 0.2;
        },
        onClickStartPartner() {
            window.open(sprintf('/%1$s/login?redirect=/%1$s/partner', window.locale), '_self');
        },
        onClickPartner() {
            window.open(sprintf('/%s/partner', window.locale), '_self');
        }
    },
    watch: {
        model: {
            handler() {
                this.calcEarnings();
            },
            deep: true
        },
    },
    components: {
        RadioButton,
        Slider,
        Button,
    }
}
</script>

<style scoped>
.calculator_earnings__form {
    background-color: #fff;
    padding: 40px;
    border-radius: 20px;
    box-shadow: 0 10px 40px 0 #000000B2;
    position: relative;
    z-index: 1;
}
.calculator_earnings__form:before {
    content: '';
    position: absolute;
    top: -60px;
    right: 30px;
    width: 138px;
    height: 131px;
    background-image: url("/i/pages/referal/calculator-money-1.webp");
    background-size: contain;
    background-repeat: no-repeat;
}
.field-radiobutton {
    padding: 20px 16px 20px 12px;
    background-color: #EBF1F6;
    border-radius: 16px;
    transition: all .2s;
}
.calculator_earnings__active_tariff {
    outline: 1px solid var(--primary-color);
}
.field-radiobutton:not(.calculator_earnings__active_tariff) .calculator_earnings__tariff_name {
    color: var(--text-color-secondary);
}
.calculator_earnings__earnings_content {
    box-shadow: 0 14px 20px 0 #89A76333;
    border: 1px solid #9CD65133;
    padding: 24px 40px 32px 40px;
    border-radius: 10px;
}
.calculator_earnings__sliders {
    margin-left: 10%;
}
.calculator_earnings__notify {
    position: relative;
}
.calculator_earnings__notify:before {
    content: '';
    position: absolute;
    top: -60px;
    right: 30px;
    width: 122px;
    height: 108px;
    background-image: url("/i/pages/referal/calculator-money-2.webp");
    background-size: contain;
    background-repeat: no-repeat;
}
.calculator_earnings__notify p {
    color: #A4B1BE;
    text-align:center;
    position: relative;
}
.calculator_earnings__example span {
    color: #A4B1BE;
}
.calculator_earnings__separator {
    height: 1px;
    background-color: #A4B1BE;
    margin: 20px 0;
}
.calculator_earnings__tops {
    position: relative;
}
.calculator_earnings__tops:before {
    content: '';
    position: absolute;
    top: 40%;
    left: 77%;
    width: 172px;
    height: 153px;
    background-image: url("/i/pages/referal/calculator-money-3.webp");
    background-size: contain;
    background-repeat: no-repeat;
}
.calculator_earnings__tops:after {
    content: '';
    position: absolute;
    top: 82%;
    left: -10%;
    width: 220px;
    height: 210px;
    background-image: url("/i/pages/referal/calculator-money-4.webp");
    background-size: contain;
    background-repeat: no-repeat;
}
@media (max-width: 1199px) {
    .calculator_earnings__sliders {
        margin-left: 0;
    }
    .calculator_earnings__notify:before {
        top: -100px;
    }
}
@media (max-width: 767px) {
    .calculator_earnings__tops:before {
        opacity: .3;
    }
}
</style>
